import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Handle,
    Position,
    BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    Save,
    Loader2,
    Play,
    ChevronLeft,
    MessageSquare,
    ListTree,
    UserRound,
    Plus,
    X,
    Settings2,
    Send,
    Bot
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import api from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';

// --- Custom Node Components ---

const ChoiceNode = ({ data }) => {
    const { t } = useLanguage();
    return (
        <div className="px-4 py-3 shadow-xl rounded-2xl bg-white border-2 border-slate-100 min-w-[200px]">
            <Handle type="target" position={Position.Top} className="w-3 h-3 bg-blue-400 !border-white" />
            <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-purple-100 rounded-lg">
                    <ListTree className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('flow.multipleChoice')}</span>
            </div>
            <div className="text-sm font-bold text-slate-900 mb-3">{data.label || t('flow.selectOption')}</div>

            <div className="space-y-2">
                {(data.choices || [`${t('flow.option')} 1`, `${t('flow.option')} 2`]).map((choice, i) => (
                    <div key={i} className="relative group">
                        <div className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs text-slate-600 font-medium group-hover:border-blue-200 transition-all flex items-center justify-between">
                            {choice}
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-200 group-hover:bg-blue-400" />
                        </div>
                        <Handle
                            type="source"
                            position={Position.Right}
                            id={`choice-${i}`}
                            style={{ top: '50%', right: '-12px' }}
                            className="w-3 h-3 bg-slate-300 !border-white hover:bg-blue-400 transition-colors"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const HandoffNode = ({ data }) => {
    const { t } = useLanguage();
    return (
        <div className="px-5 py-4 shadow-xl rounded-2xl bg-slate-900 border-2 border-slate-800 border-l-4 border-l-amber-500 min-w-[180px]">
            <Handle type="target" position={Position.Top} className="w-3 h-3 bg-amber-400 !border-slate-900" />
            <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20">
                    <UserRound className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                    <h4 className="text-[10px] font-black text-amber-500 uppercase tracking-tighter">{t('flow.specialAction')}</h4>
                    <p className="text-white font-bold text-sm">{t('flow.escalateToHuman')}</p>
                </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed italic">
                {t('flow.escalateDescription')}
            </p>
        </div>
    );
};

const TextNode = ({ data }) => {
    const { t } = useLanguage();
    return (
        <div className="px-5 py-4 shadow-xl rounded-2xl bg-white border-2 border-slate-100 border-l-4 border-l-blue-600 min-w-[200px]">
            <Handle type="target" position={Position.Top} className="w-3 h-3 bg-blue-400 !border-white" />
            <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('flow.aiResponse')}</span>
            </div>
            <div className="text-sm font-medium text-slate-700 leading-relaxed italic">
                {data.label || t('flow.writeBotMessage')}
            </div>
            <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-blue-400 !border-white" />
        </div>
    );
};

const nodeTypes = {
    text: TextNode,
    choice: ChoiceNode,
    handoff: HandoffNode,
};

// --- Main Flow Editor ---

const FlowEditor = () => {
    const { botId } = useParams();
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [botName, setBotName] = useState('Meu Bot');
    const [lastSaved, setLastSaved] = useState(null);
    const [testOpen, setTestOpen] = useState(false);
    const [testMessages, setTestMessages] = useState([]);
    const [testInput, setTestInput] = useState('');
    const [testSending, setTestSending] = useState(false);
    const [testConvId, setTestConvId] = useState(null);
    const autoSaveTimer = useRef(null);
    const messagesEndRef = useRef(null);
    const { t, language } = useLanguage();

    // Load Bot Flow
    useEffect(() => {
        const fetchBot = async () => {
            try {
                const response = await api.get('/api/bots');
                if (response.ok) {
                    const bots = await response.json();
                    const currentBot = bots.find(b => b.bot_id === botId);
                    if (currentBot) {
                        setBotName(currentBot.name);
                        if (currentBot.flow_graph && currentBot.flow_graph.nodes) {
                            setNodes(currentBot.flow_graph.nodes);
                            setEdges(currentBot.flow_graph.edges || []);
                        } else {
                            // Initial default node
                            setNodes([{ id: '1', position: { x: 250, y: 100 }, data: { label: t('flow.welcomeMessage') }, type: 'text' }]);
                        }
                    }
                }
            } catch (err) {
                console.error("Error loading bot flow:", err);
            } finally {
                setLoading(false);
            }
        };
        if (botId) fetchBot();
    }, [botId, setNodes, setEdges, t]);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge(params, eds)),
        [setEdges],
    );

    const addNode = (type) => {
        const id = (nodes.length + 1).toString();
        const newNode = {
            id,
            type,
            position: { x: Math.random() * 400 + 100, y: Math.random() * 400 + 100 },
            data: {
                label: type === 'handoff' ? t('flow.humanHandoff') : `${t('flow.newMessage')} ${id}`,
                choices: type === 'choice' ? [t('flow.yesInterested'), t('flow.noThanks')] : undefined
            },
        };
        setNodes((nds) => nds.concat(newNode));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const flowGraph = { nodes, edges };
            await api.post('/api/bots', {
                bot_id: botId,
                name: botName,
                flow_graph: flowGraph
            });
            setLastSaved(new Date());
        } catch (err) {
            console.error("Error saving flow:", err);
        } finally {
            setSaving(false);
        }
    };

    // Auto-save every 30 seconds when changes happen
    useEffect(() => {
        if (loading || nodes.length === 0) return;
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => {
            handleSave();
        }, 30000);
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    }, [nodes, edges]); // eslint-disable-line react-hooks/exhaustive-deps

    // Test chat
    const sendTestMessage = async () => {
        if (!testInput.trim() || testSending) return;
        const userMsg = { role: 'user', content: testInput };
        setTestMessages(prev => [...prev, userMsg]);
        setTestInput('');
        setTestSending(true);
        try {
            const response = await api.post(`/api/chat/${botId}`, {
                message: testInput,
                conversation_id: testConvId
            });
            if (response.ok) {
                const data = await response.json();
                setTestConvId(data.conversation_id);
                setTestMessages(prev => [...prev, { role: 'assistant', content: data.assistant_message.content }]);
            }
        } catch (err) {
            setTestMessages(prev => [...prev, { role: 'assistant', content: t('flow.errorProcessing') }]);
        } finally {
            setTestSending(false);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [testMessages]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-[#f8fafc] overflow-hidden">
            {/* Top Bar Editor */}
            <div className="h-16 border-b border-slate-200 px-6 flex items-center justify-between bg-white z-10 shadow-sm">
                <div className="flex items-center gap-4">
                    <button onClick={() => window.history.back()} className="p-2 hover:bg-slate-50 rounded-xl transition-colors">
                        <ChevronLeft className="h-5 w-5 text-slate-500" />
                    </button>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-bold text-slate-900 leading-none">{botName}</h1>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-black uppercase rounded">{t('flow.editorBeta')}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1 uppercase font-black tracking-widest">{botId}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={() => { setTestOpen(!testOpen); setTestMessages([]); setTestConvId(null); }}
                        className={`h-10 px-4 text-xs font-bold gap-2 transition-all ${testOpen ? 'bg-blue-50 border-blue-200 text-blue-600' : 'hover:bg-slate-50'}`}
                    >
                        <Play className="h-3 w-3 fill-current" /> {t('flow.testWithAI')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold gap-2 shadow-lg shadow-blue-100 transition-all disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? t('flow.saving') : t('flow.saveChanges')}
                    </Button>
                </div>
            </div>

            {/* Canvas Area */}
            <div className="flex-1 relative">
                {/* Floating Tool Palette */}
                <div className="absolute top-6 left-6 z-20 flex flex-col gap-3">
                    <Card className="p-2 shadow-2xl border-none bg-white/90 backdrop-blur-md rounded-2xl flex flex-col gap-1 items-center">
                        <button
                            onClick={() => addNode('text')}
                            className="p-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all group relative"
                        >
                            <MessageSquare className="h-5 w-5" />
                            <span className="absolute left-16 bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded hidden group-hover:block whitespace-nowrap">{t('flow.textMessage')}</span>
                        </button>
                        <button
                            onClick={() => addNode('choice')}
                            className="p-3 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-600 hover:text-white transition-all group relative"
                        >
                            <ListTree className="h-5 w-5" />
                            <span className="absolute left-16 bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded hidden group-hover:block whitespace-nowrap">{t('flow.choiceButtons')}</span>
                        </button>
                        <button
                            onClick={() => addNode('handoff')}
                            className="p-3 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white transition-all group relative"
                        >
                            <UserRound className="h-5 w-5" />
                            <span className="absolute left-16 bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded hidden group-hover:block whitespace-nowrap">{t('flow.humanHandoff')}</span>
                        </button>
                        <div className="h-px w-full bg-slate-100 my-1" />
                        <button className="p-3 text-slate-400 hover:bg-slate-100 rounded-xl transition-all">
                            <Settings2 className="h-5 w-5" />
                        </button>
                    </Card>
                </div>

                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    nodeTypes={nodeTypes}
                    fitView
                    colorMode="light"
                >
                    <Controls />
                    <MiniMap />
                    <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#cbd5e1" />
                </ReactFlow>

                <div className="absolute bottom-6 right-6 z-20">
                    <div className="bg-white/80 backdrop-blur-md px-4 py-2 rounded-full border border-slate-100 shadow-lg text-[10px] font-bold text-slate-400 flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${saving ? 'bg-amber-500 animate-pulse' : 'bg-green-500 animate-pulse'}`} />
                        {saving ? t('flow.saving') : lastSaved ? `${t('flow.savedAt')} ${lastSaved.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}` : t('flow.autosaveActive')}
                    </div>
                </div>

                {/* Test Chat Panel */}
                {testOpen && (
                    <div className="absolute top-6 right-6 z-20 w-80 h-[500px] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                        <div className="px-4 py-3 bg-slate-900 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Bot className="h-4 w-4 text-blue-400" />
                                <span className="text-white text-xs font-bold">{t('flow.testWithAI')} - {botName}</span>
                            </div>
                            <button onClick={() => setTestOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                            {testMessages.length === 0 && (
                                <div className="text-center text-slate-400 text-xs mt-8">
                                    <Bot className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                    <p>{t('flow.sendMessage')}</p>
                                </div>
                            )}
                            {testMessages.map((msg, i) => (
                                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm shadow-sm'}`}>
                                        {msg.content}
                                    </div>
                                </div>
                            ))}
                            {testSending && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-slate-200 px-3 py-2 rounded-2xl rounded-bl-sm shadow-sm">
                                        <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className="p-3 border-t border-slate-200 bg-white">
                            <div className="flex gap-2">
                                <input
                                    value={testInput}
                                    onChange={(e) => setTestInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && sendTestMessage()}
                                    placeholder={t('flow.typeMessage')}
                                    className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                                <button
                                    onClick={sendTestMessage}
                                    disabled={testSending || !testInput.trim()}
                                    className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                    <Send className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FlowEditor;
